import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AiBanner } from '@factorialco/f0-react/dist/experimental'
import {
  FormInsightsOverviewElementFragment,
  FormTextElementSummaryDocument,
  SurveyParticipationDocument,
} from 'generated/resources/graphql'

import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ChartWidget } from 'modules/engagement_surveys/components/FormInsightsOverview/InsightsVisualization/ChartWidget'
import type { FormFiltersStorage } from 'modules/forms'

interface Props {
  element: FormInsightsOverviewElementFragment
  activeFilters?: FormFiltersStorage['filters']
  cycleIds?: number[]
}

export function LongText({ element, activeFilters, cycleIds }: Props) {
  const formId = element.formId
  const [summary, setSummary] = useState<string | null>(null)

  // Check if there are answers available (without generating AI summary)
  const { data: participationData, loading: participationLoading } = useQuery(
    SurveyParticipationDocument,
    {
      variables: {
        formId,
        cycleIds: cycleIds?.map(String) || [],
        ...activeFilters,
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  const hasAnswers = (participationData?.forms.answered?.totalCount || 0) > 0

  const [fetchData, { data, called, loading }] = useLazyQuery(FormTextElementSummaryDocument, {
    variables: {
      elementId: element.id,
      ...activeFilters,
      cycleIds,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    // Update summary when query completes
    if (called && !loading) {
      const aiSummary = data?.forms.answerAiInsightsConnection?.nodes?.[0]?.summary
      setSummary(aiSummary || '')
    }
  }, [data, called, loading])

  const hasNoData = !hasAnswers && !participationLoading
  const isLoading = (called && loading) || participationLoading

  return (
    <ChartWidget
      title={element.label}
      description={element.description || ''}
      questionType={element.type}
    >
      {summary ? (
        <F0AiBanner title={i18n.t('forms.analytics.ai_summary.title')} content={summary} />
      ) : (
        <Box gap="s8" padding="s8" flexDirection="column" alignItems="flexStart">
          {hasNoData && (
            <Text size="200" color="grey700" weight="medium">
              {i18n.t('forms.analytics.ai_summary.not_enough_responses')}
            </Text>
          )}
          <F0Button
            variant="outline"
            onClick={() => fetchData()}
            label={i18n.t('forms.analytics.ai_summary.generate_summary')}
            disabled={hasNoData || isLoading}
            loading={isLoading}
          />
        </Box>
      )}
    </ChartWidget>
  )
}
